import React, { useId, useRef, useState } from "react";
import { functions } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import ReceiptPrinterControls from "./ReceiptPrinterControls";
import RestaurantPrinterRoutingControls from "./RestaurantPrinterRoutingControls";
import StaffDeviceControls from "./StaffDeviceControls";
import TerminalRecoveryControls from "./TerminalRecoveryControls";
import TerminalSetupControls from "./TerminalSetupControls";
import RecoveryOperationsControls from "./RecoveryOperationsControls";
import RestaurantSetupReadiness from "./RestaurantSetupReadiness";
import DeviceDiagnosticsControls from "./DeviceDiagnosticsControls";
import HardwareSupportControls from "./HardwareSupportControls";
import HardwareConnectionControls from "./HardwareConnectionControls";
import ServiceSessionSupportControls from "./ServiceSessionSupportControls";
import "./styles/RestaurantAdvancedSettings.css";

const PROVISIONING_TABS = [
	{ id: "printers", label: "Printers & Drawers" },
	{ id: "devices", label: "Devices & Enrollment" },
];
const SHARED_TABS = [
	{ id: "payments", label: "Payments & Terminals" },
	{ id: "recovery", label: "Recovery" },
];

function AdvancedSettingsSections({ restaurantId, environment, paymentSettings, onPrinterSaved, onTerminalSaved, initialSection }) {
	const tabs = environment ? [...PROVISIONING_TABS, ...SHARED_TABS] : SHARED_TABS;
	const initial = ["devices", "printers", "recovery"].includes(initialSection) && tabs.some(tab => tab.id === initialSection)
		? initialSection : tabs[0].id;
	const [selected, setSelected] = useState(initial);
	const [focused, setFocused] = useState(initial);
	const [readinessOpen, setReadinessOpen] = useState(false);
	const [routingOpen, setRoutingOpen] = useState(false);
	const tabRefs = useRef([]);
	const instanceId = useId();
	const tabId = (id) => `${instanceId}-tab-${id}`;
	const panelId = (id) => `${instanceId}-panel-${id}`;
	const activate = (id) => { setSelected(id); setFocused(id); };
	const handleKeyDown = (event, index) => {
		if (event.altKey || event.ctrlKey || event.metaKey) return;
		let nextIndex;
		switch (event.key) {
			case "ArrowRight": nextIndex = (index + 1) % tabs.length; break;
			case "ArrowLeft": nextIndex = (index - 1 + tabs.length) % tabs.length; break;
			case "Home": nextIndex = 0; break;
			case "End": nextIndex = tabs.length - 1; break;
			case "Enter":
			case " ":
				event.preventDefault();
				activate(tabs[index].id);
				return;
			default: return;
		}
		event.preventDefault();
		// Focus navigation does not mount controls that may fetch remote data.
		setFocused(tabs[nextIndex].id);
		tabRefs.current[nextIndex]?.focus();
	};

	return <section className="restaurant-advanced-settings" aria-labelledby={`${instanceId}-heading`}>
		<h2 id={`${instanceId}-heading`} className="restaurant-advanced-settings-heading">Advanced settings</h2>
		<div className="restaurant-advanced-settings-tablist" role="tablist" aria-label="Advanced settings" aria-orientation="horizontal">
			{tabs.map((tab, index) => <button key={tab.id} type="button" role="tab" id={tabId(tab.id)}
				className="restaurant-advanced-settings-tab" aria-controls={panelId(tab.id)} aria-selected={selected === tab.id}
				tabIndex={focused === tab.id ? 0 : -1} ref={(element) => { tabRefs.current[index] = element; }}
				onFocus={() => setFocused(tab.id)} onClick={() => activate(tab.id)} onKeyDown={(event) => handleKeyDown(event, index)}>
				{tab.label}
			</button>)}
		</div>
		<div key={selected} className="restaurant-advanced-settings-panel" role="tabpanel" id={panelId(selected)}
			aria-labelledby={tabId(selected)} tabIndex={0}>
			{selected === "printers" && <>
				<HardwareConnectionControls restaurantId={restaurantId} />
				<HardwareSupportControls restaurantId={restaurantId} />
				<ReceiptPrinterControls key={restaurantId} restaurantId={restaurantId} onSaved={onPrinterSaved} />
				<button type="button" className="printer-routing-toggle" aria-expanded={routingOpen} aria-controls={`${instanceId}-printer-routing`}
					onClick={() => setRoutingOpen(open => !open)}>Shared printer library</button>
				<div id={`${instanceId}-printer-routing`} hidden={!routingOpen}>
					{routingOpen && <RestaurantPrinterRoutingControls restaurantId={restaurantId} />}
				</div>
			</>}
			{selected === "devices" && <>
				<DeviceDiagnosticsControls restaurantId={restaurantId} />
				<StaffDeviceControls key={restaurantId} restaurantId={restaurantId} />
			</>}
			{selected === "payments" && <>
				{paymentSettings}
				{environment === "Production" && functions.app.options.projectId === "scervmvp" &&
					<TerminalSetupControls restaurantId={restaurantId} onSaved={onTerminalSaved} />}
				<TerminalRecoveryControls key={restaurantId} restaurantId={restaurantId} />
			</>}
			{selected === "recovery" && <>
				{environment && <ServiceSessionSupportControls restaurantId={restaurantId} financialRecoveryPanelId={panelId("financial-recovery")} />}
				<div id={panelId("financial-recovery")} className="service-session-financial-recovery" role="region" aria-label="Financial recovery" tabIndex={-1}>
					<RecoveryOperationsControls key={restaurantId} restaurantId={restaurantId} />
				</div>
			</>}
		</div>
		{environment && <details className="restaurant-setup-disclosure" onToggle={(event) => setReadinessOpen(event.currentTarget.open)}>
			<summary>Setup readiness</summary>
			{readinessOpen && <RestaurantSetupReadiness restaurantId={restaurantId} onOpenSection={(id) => {
				const index = tabs.findIndex((tab) => tab.id === id);
				if (index < 0) return;
				activate(id);
				tabRefs.current[index]?.focus();
			}} />}
		</details>}
	</section>;
}

export default function RestaurantAdvancedSettings({ restaurantId, paymentSettings, onPrinterSaved, onTerminalSaved, initialSection }) {
	const environment = getStaffProvisioningEnvironment();
	return <AdvancedSettingsSections key={`${restaurantId}:${environment || "standard"}`} restaurantId={restaurantId}
		environment={environment} paymentSettings={paymentSettings} onPrinterSaved={onPrinterSaved} onTerminalSaved={onTerminalSaved} initialSection={initialSection} />;
}
